import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import useApi from "../../hooks/useApi";
import { useQuery, useMutation, useQueryClient } from "react-query";
import usePagination from "../../hooks/usePagination";
import moment from "moment";

import ClientPolicyRequirementInfo from "../clients/ClientPolicyRequirementInfo";
import PolicyItemStatus from "../../widgets/PolicyItemStatus";

const ClientDashboard = () => {
  const { Paginate, searchParamString } = usePagination();
  const { api, abortSignal, auth } = useApi();

  const getAccount = async () => {
    try {
      const url = `/clients/client-policies`;
      const { data } = await api.list(url);
      return data;
    } catch (error) {
      throw new Error("failed to fetch records");
    }
  };

  const { data: account } = useQuery(["clientPolicy", auth], ({ queryKey }) =>
    getAccount()
  );

  const getPolicies = async (client) => {
    try {
      const url = `/policies/clients/${client?.info?.id}/expiring`;
      const { data } = await api.list(url);
      return data;
    } catch (error) {
      throw new Error("failed to fetch records");
    }
  };

  const { data: policies } = useQuery(
    ["expiringPolicies", account, auth],
    ({ queryKey }) => getPolicies(queryKey[1])
  );

  return (
    <section id="content" className="content">
      <div className="content__header content__boxed overlapping">
        <div className="content__wrap">
          {/* <!-- Page title and information --> */}
          <h1 className="page-title mb-2">Dashboard</h1>
          <h2 className="h5">Welcome back to the Dashboard.</h2>
          <p>
            {/* Scroll down to see quick links and overviews of your Server, To do
            list
            <br />
            Order status or get some Help using Nifty. */}
          </p>
          {/* <!-- END : Page title and information --> */}
        </div>
      </div>
      <div className="content__boxed">
        <div className="content__wrap">
          <div className="row">
            <div className="col-xl-12">
              <div className="row">
                <div className="col-sm-3">
                  {/* <!-- Tile - HDD Usage --> */}
                  <div className="card bg-success text-white overflow-hidden mb-3">
                    <div className="p-3 pb-2">
                      <h5 className="mb-3">
                        <i className="demo-psi-data-storage text-reset text-opacity-75 fs-3 me-2"></i>
                        <Link
                          to="/client-account/policies/active"
                          style={{ color: "#FFF", textDecoration: "none" }}
                        >
                          Active Policies
                        </Link>
                      </h5>
                      <ul className="list-group list-group-borderless">
                        <li className="list-group-item p-0 text-reset d-flex justify-content-between align-items-start">
                          <div className="me-auto">Count</div>
                          <span className="fw-bold">
                            {account?.stats?.policyCount || 0}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  {/* <!-- END : Tile - HDD Usage --> */}
                </div>
                <div className="col-sm-3">
                  {/* <!-- Tile - Earnings --> */}
                  <div className="card bg-info text-white overflow-hidden mb-3">
                    <div className="p-3 pb-2">
                      <h5 className="mb-3">
                        <i className="demo-psi-coin text-reset text-opacity-75 fs-2 me-2"></i>
                        <Link
                          to="/client-account/claims/?status=pending"
                          style={{ color: "#FFF", textDecoration: "none" }}
                        >
                          Claims
                        </Link>
                        {/* Claims */}
                      </h5>
                      <ul className="list-group list-group-borderless">
                        <li className="list-group-item p-0 text-reset d-flex justify-content-between align-items-start">
                          <div className="me-auto">Pending</div>
                          <span className="fw-bold">
                            {account?.stats?.claimCount || 0}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  {/* <!-- END : Tile - Earnings --> */}
                </div>
                <div className="col-sm-3">
                  {/* <!-- Tile - Sales --> */}
                  <div className="card bg-danger text-white overflow-hidden mb-3">
                    <div className="p-3 pb-2">
                      <h5 className="mb-3">
                        <i className="demo-psi-basket-coins text-reset text-opacity-75 fs-2 me-2"></i>
                        <Link
                          to="/client-account/policies/expired"
                          style={{ color: "#FFF", textDecoration: "none" }}
                        >
                          Expired Policies
                        </Link>
                      </h5>
                      <ul className="list-group list-group-borderless">
                        <li className="list-group-item p-0 text-reset d-flex justify-content-between align-items-start">
                          <div className="me-auto">Count</div>
                          <span className="fw-bold">
                            {account?.stats?.expiredCount || 0}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  {/* <!-- END : Tile - Sales --> */}
                </div>
                <div className="col-sm-3">
                  {/* <!-- Tile - Task Progress --> */}
                  <div className="card bg-warning text-white overflow-hidden mb-3">
                    <div className="p-3 pb-2">
                      <h5 className="mb-3">
                        <i className="demo-psi-basket-coins text-reset text-opacity-75 fs-2 me-2"></i>
                        <Link
                          to="/client-account/policies/expiring"
                          style={{ color: "#FFF", textDecoration: "none" }}
                        >
                          Expiring Policies
                        </Link>
                      </h5>
                      <ul className="list-group list-group-borderless">
                        <li className="list-group-item p-0 text-reset d-flex justify-content-between align-items-start">
                          <div className="me-auto">Expiring within 30 days</div>
                          <span className="fw-bold">
                            {account?.stats?.expiringCount || 0}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  {/* <!-- END : Tile - Task Progress --> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="content__boxed bg-gray-500 my-3 pt-3">
        <div className="content__wrap">
          <div className="row gx-5 gy-5 gy-md-0">
            <h2>Expiring Policies</h2>
            <>
              <div className="table-responsive bg-white">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Info</th>
                      <th>Type</th>
                      <th>Company</th>
                      <th>Expires</th>
                      <th>Policy Number</th>
                      <th className="text-center">Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {policies &&
                      policies.map((policy) => (
                        <tr key={policy.id}>
                          <td>
                            {<ClientPolicyRequirementInfo policy={policy} />}
                          </td>
                          <td>
                            {policy?.PolicyType?.name}
                            <br />
                            {policy?.Product?.name}
                          </td>
                          <td>
                            <span className="fw-bold">
                              {policy?.Company?.name}
                            </span>
                          </td>
                          <td>
                            <>
                              <span>
                                {moment(policy?.expires).format("DD/MM/YYYY") ||
                                  ""}
                              </span>
                              <p className="text-muted">
                                {policy?.expires &&
                                  moment().to(moment(policy?.expires))}
                              </p>
                            </>
                          </td>
                          <td>{policy?.policyNumber}</td>

                          <td className="text-center fs-5">
                            <PolicyItemStatus status={policy?.status} />
                          </td>
                          <td className="text-center">
                            <div className="d-flex flex-wrap justify-content-center">
                              <div className="btn-group">
                                <Link
                                  to={`/client-account/policy-item/${policy?.id}`}
                                  title="View policy"
                                  className="btn btn-icon btn-outline-primary"
                                >
                                  <i className="bi bi-pencil-square fs-4 text-success"></i>
                                </Link>
                                {/* )} */}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                <Paginate />
              </div>
            </>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientDashboard;
